/**
 * Camera maths for the floor-plan canvas, kept pure so pan, zoom, and hit coordinates can be
 * tested without a DOM and stay identical whichever renderer paints the plan.
 *
 * The view box always carries the canvas's own aspect ratio, so the SVG never letterboxes and the
 * screen/world conversion is a single uniform scale rather than a `preserveAspectRatio` guess.
 */
import { type FloorPlanPoint, SIXTEENTHS_PER_FOOT } from "@/lib/shows/floorPlan";

export type ViewBox = { x: number; y: number; width: number; height: number; };
export type CanvasSize = { width: number; height: number; };
export type Bounds = { x: number; y: number; width: number; height: number; };

/** Closest the camera may go, as world units across one screen pixel. */
const MIN_UNITS_PER_PIXEL = SIXTEENTHS_PER_FOOT / 96;
/** How far past framing the whole plan the camera may pull back. */
const FURTHEST_OUT = 2;
/** Only for an empty plan, where there is no content to measure the floor against. */
const EMPTY_MAX_UNITS_PER_PIXEL = SIXTEENTHS_PER_FOOT * 8;
/** The one padding every fit leaves around the content. */
const FIT_PADDING = 0.08;
/** A hall with nothing in it still needs a stage to draw on. */
export const DEFAULT_WORLD = { width: 60 * SIXTEENTHS_PER_FOOT, height: 40 * SIXTEENTHS_PER_FOOT };

export function unitsPerPixel(view: ViewBox, size: CanvasSize): number {
  return size.width > 0 ? view.width / size.width : 1;
}

/** Zoom as a percentage, where 100% means one foot of hall per 12 screen pixels. */
export function zoomPercent(view: ViewBox, size: CanvasSize): number {
  const perPixel = unitsPerPixel(view, size);
  return perPixel > 0 ? Math.round((SIXTEENTHS_PER_FOOT / 12 / perPixel) * 100) : 100;
}

/**
 * The one definition of the width a fit lands on for this content, before ANY clamp - the natural
 * fit. `fitViewBox` frames with it, and `clampToLimits` sizes its content-relative ceiling from
 * it; asking `fitViewBox` for that ceiling instead would recurse into the very clamp it is sizing.
 */
function naturalFitWidth(
  content: { width: number; height: number; } | null | undefined,
  size: CanvasSize,
): number {
  const safe = {
    width: size.width > 0 ? size.width : 1,
    height: size.height > 0 ? size.height : 1,
  };
  const aspect = safe.width / safe.height;
  const area = content && content.width > 0 && content.height > 0
    ? content
    : DEFAULT_WORLD;
  const scale = 1 + 2 * FIT_PADDING;
  const padded = { width: area.width * scale, height: area.height * scale };
  return Math.max(padded.width, padded.height * aspect);
}

/*
 * The floor is relative to what is on the plan, not to a fixed world size: a hall may be a
 * corridor or a convention centre, and either way pulling back to twice what it takes to frame it
 * is as far as anyone can read. Recomputed from `content` on every zoom, so it follows the plan as
 * it grows. Panning stays unbounded - this bounds how small the content may be drawn, not where
 * the camera may look.
 */
function clampToLimits(view: ViewBox, size: CanvasSize, content?: Bounds | null): ViewBox {
  if (size.width <= 0 || size.height <= 0) return view;
  const perPixel = view.width / size.width;
  const furthest = content
    ? Math.max(
      MIN_UNITS_PER_PIXEL * FURTHEST_OUT,
      naturalFitWidth(content, size) / size.width * FURTHEST_OUT,
    )
    : EMPTY_MAX_UNITS_PER_PIXEL;
  const clamped = Math.min(furthest, Math.max(MIN_UNITS_PER_PIXEL, perPixel));
  if (clamped === perPixel) return view;
  const width = clamped * size.width;
  const height = clamped * size.height;
  return {
    x: view.x + (view.width - width) / 2,
    y: view.y + (view.height - height) / 2,
    width,
    height,
  };
}

/**
 * Frames `content` inside `size` with padding, matching the canvas aspect exactly.
 *
 * Its own clamp is given `content` too (TRO-1276), so a plan big enough to need it - a
 * convention centre, or a room scaled up by a mistyped square footage - still gets a real fit
 * rather than being held to the ceiling meant for an EMPTY plan, which "zoom to fit" and every
 * zoom-out step otherwise inherited no matter how large the plan actually was.
 */
export function fitViewBox(
  content: { x: number; y: number; width: number; height: number; } | null,
  size: CanvasSize,
): ViewBox {
  const safe = {
    width: size.width > 0 ? size.width : 1,
    height: size.height > 0 ? size.height : 1,
  };
  const aspect = safe.width / safe.height;
  /*
   * "Is there anything to measure against?" asked once: the width, where that width is centred,
   * and the ceiling the clamp holds it to all have to answer it the same way, or the fit lands on
   * one plan's size and is then shrunk by a ceiling sized for another.
   */
  const measured = content && content.width > 0 && content.height > 0 ? content : null;
  const area = measured ?? { x: 0, y: 0, width: DEFAULT_WORLD.width, height: DEFAULT_WORLD.height };
  const width = naturalFitWidth(measured, size);
  const height = width / aspect;
  return clampToLimits(
    {
      x: area.x + area.width / 2 - width / 2,
      y: area.y + area.height / 2 - height / 2,
      width,
      height,
    },
    size,
    measured,
  );
}

/** Reshapes a view box to a new canvas aspect, such as a resized window, holding its centre. */
export function reframeViewBox(
  view: ViewBox,
  size: CanvasSize,
  content?: Bounds | null,
): ViewBox {
  if (size.width <= 0 || size.height <= 0 || view.width <= 0) return view;
  const height = view.width * (size.height / size.width);
  if (height === view.height) return view;
  return clampToLimits(
    {
      x: view.x,
      y: view.y + (view.height - height) / 2,
      width: view.width,
      height,
    },
    size,
    content,
  );
}

export function screenToWorld(
  screen: { x: number; y: number; },
  view: ViewBox,
  size: CanvasSize,
): FloorPlanPoint {
  const perPixel = unitsPerPixel(view, size);
  return {
    x: Math.round(view.x + screen.x * perPixel),
    y: Math.round(view.y + screen.y * perPixel),
  };
}

/**
 * Zooms by `factor` while pinning `screen` to the world point already under it, so wheel zoom
 * centres on the cursor rather than the window.
 */
export function zoomViewBoxAt(
  view: ViewBox,
  screen: { x: number; y: number; },
  factor: number,
  size: CanvasSize,
  content?: Bounds | null,
): ViewBox {
  if (!Number.isFinite(factor) || factor <= 0) return view;
  const anchor = screenToWorld(screen, view, size);
  const width = view.width / factor;
  const height = view.height / factor;
  const next = clampToLimits(
    {
      x: anchor.x - (anchor.x - view.x) / factor,
      y: anchor.y - (anchor.y - view.y) / factor,
      width,
      height,
    },
    size,
    content,
  );
  if (next.width === view.width) return view;
  // Re-pin after clamping so hitting a zoom limit does not also drift the canvas sideways.
  const perPixel = next.width / (size.width || 1);
  return {
    ...next,
    x: anchor.x - screen.x * perPixel,
    y: anchor.y - screen.y * perPixel,
  };
}

/** Pans by a screen-space delta, in the direction the content should travel. */
export function panViewBox(
  view: ViewBox,
  screenDelta: { x: number; y: number; },
  size: CanvasSize,
): ViewBox {
  const perPixel = unitsPerPixel(view, size);
  return {
    ...view,
    x: view.x + screenDelta.x * perPixel,
    y: view.y + screenDelta.y * perPixel,
  };
}

export function zoomViewBoxCentre(
  view: ViewBox,
  factor: number,
  size: CanvasSize,
  content?: Bounds | null,
): ViewBox {
  return zoomViewBoxAt(view, { x: size.width / 2, y: size.height / 2 }, factor, size, content);
}
